import { z } from "zod";

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);

export const restoreSourceSchema = z.discriminatedUnion("sourceKind", [
  z.object({
    snapshotId: z.uuid(),
    sourceKind: z.literal("retained_snapshot"),
  }),
  z.object({ importId: z.uuid(), sourceKind: z.literal("uploaded_archive") }),
]);
export type RestoreSource = z.infer<typeof restoreSourceSchema>;

export const uploadedRestoreImportStatusSchema = z.enum([
  "pending_upload",
  "uploaded",
  "validating",
  "ready",
  "invalid",
  "expired",
  "aborted",
]);
export type UploadedRestoreImportStatus = z.infer<
  typeof uploadedRestoreImportStatusSchema
>;

export const createUploadedRestoreImportRequestSchema = z.object({
  archiveByteSize: z
    .int()
    .positive()
    .max(256 * 1024 * 1024),
  archiveSha256: sha256Schema,
  fileName: z
    .string()
    .min(1)
    .max(255)
    .refine(
      (value) =>
        value.toLowerCase().endsWith(".zip") &&
        !value.includes("/") &&
        !value.includes("\\") &&
        !value.includes("\0"),
      "Upload filename must be a basename ending in .zip.",
    ),
  idempotencyKey: z.uuid(),
});
export type CreateUploadedRestoreImportRequest = z.infer<
  typeof createUploadedRestoreImportRequestSchema
>;

export const uploadedRestoreImportSchema = z
  .object({
    archiveByteSize: z
      .int()
      .positive()
      .max(256 * 1024 * 1024),
    archiveSha256: sha256Schema,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    importId: z.uuid(),
    originalSnapshotId: z.uuid().nullable(),
    sourceBoardId: z
      .string()
      .regex(/^[a-f0-9]{24}$/)
      .nullable(),
    sourceKind: z.literal("uploaded_archive"),
    status: uploadedRestoreImportStatusSchema,
    validationCode: z
      .string()
      .regex(/^[a-z][a-z0-9_]{2,63}$/)
      .nullable(),
  })
  .superRefine((value, context) => {
    if (Date.parse(value.expiresAt) <= Date.parse(value.createdAt)) {
      context.addIssue({
        code: "custom",
        message: "Upload expiry must follow creation.",
        path: ["expiresAt"],
      });
    }
    const validated = value.status === "ready";
    if (
      validated !==
      (value.originalSnapshotId !== null && value.sourceBoardId !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Only ready imports expose validated archive identity.",
      });
    }
    const invalid = value.status === "invalid";
    if (invalid !== (value.validationCode !== null)) {
      context.addIssue({
        code: "custom",
        message: "Only invalid imports contain a validation code.",
      });
    }
  });
export type UploadedRestoreImport = z.infer<typeof uploadedRestoreImportSchema>;

export const uploadedRestoreImportPolicy = {
  maxActiveImportsPerAccount: 3,
  maxActiveUploadBytesPerAccount: 512 * 1024 * 1024,
  maxArchiveBytes: 256 * 1024 * 1024,
  quarantineLifetimeSeconds: 24 * 60 * 60,
} as const;

export interface ActiveUploadedRestoreDeclaration {
  archiveByteSize: number;
  archiveSha256: string;
  fileName: string;
  idempotencyKey: string;
  importId: string;
}

export type UploadedRestoreAdmission =
  { disposition: "create" } | { disposition: "reuse"; importId: string };

export class UploadedRestoreAdmissionError extends Error {
  constructor(
    readonly code:
      | "active_import_limit"
      | "active_upload_bytes_limit"
      | "idempotency_conflict",
    message: string,
  ) {
    super(message);
    this.name = "UploadedRestoreAdmissionError";
  }
}

export function evaluateUploadedRestoreAdmission(
  requestInput: CreateUploadedRestoreImportRequest,
  activeImports: readonly ActiveUploadedRestoreDeclaration[],
): UploadedRestoreAdmission {
  const request = createUploadedRestoreImportRequestSchema.parse(requestInput);
  const existing = activeImports.find(
    ({ idempotencyKey }) => idempotencyKey === request.idempotencyKey,
  );
  if (existing) {
    if (
      existing.archiveByteSize !== request.archiveByteSize ||
      existing.archiveSha256 !== request.archiveSha256 ||
      existing.fileName !== request.fileName
    ) {
      throw new UploadedRestoreAdmissionError(
        "idempotency_conflict",
        "The idempotency key is already bound to a different upload.",
      );
    }
    return { disposition: "reuse", importId: existing.importId };
  }
  if (
    activeImports.length >=
    uploadedRestoreImportPolicy.maxActiveImportsPerAccount
  ) {
    throw new UploadedRestoreAdmissionError(
      "active_import_limit",
      "The account already has the maximum number of active uploads.",
    );
  }
  const activeBytes = activeImports.reduce(
    (total, upload) => total + upload.archiveByteSize,
    0,
  );
  if (
    activeBytes + request.archiveByteSize >
    uploadedRestoreImportPolicy.maxActiveUploadBytesPerAccount
  ) {
    throw new UploadedRestoreAdmissionError(
      "active_upload_bytes_limit",
      "The account's active upload byte quota would be exceeded.",
    );
  }
  return { disposition: "create" };
}

export function uploadedRestoreImportIsUsable(
  value: UploadedRestoreImport,
  now: Date,
): boolean {
  const parsed = uploadedRestoreImportSchema.parse(value);
  return (
    parsed.status === "ready" && Date.parse(parsed.expiresAt) > now.valueOf()
  );
}
